import { observable, action, computed, makeObservable } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import apiActions from 'api/actions';

export default class InstructorInternalCommentStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      requests: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      reset: action,
      fetchAll: action,
      fetchAllCompleted: computed,
      refetch: action,
    });

    this.root = root;
  }

  data = new Map();
  requests = new Map();

  add = data => {
    const comment = new InstructorInternalComment(data, this.root)
    this.data.set(comment.id, comment);
  }

  addMany = datas => {
    const merge = new Map();
    datas.forEach(data => {
      const comment = new InstructorInternalComment(data, this.root)
      merge.set(comment.id, comment)
    })
    this.data.merge(merge);
  }

  remove = id => {
    if (id && this.data.has(id)) this.data.delete(id);
  }

  get all() {
    return Array.from(this.data.values());
  }

  find(id) {
    return this.data.get(id);
  }

  forEvent(eventId) {
    return _.filter(this.all, ['event_id', eventId]);
  }

  forStudent(userId) {
    return _.filter(this.all, ['student_id', userId]);
  }

  forPotentialChild(potentialChildId) {
    return _.filter(this.all, ['potential_child_id', potentialChildId]);
  }

  reset = () => {
    this.data.clear();
    this.requests.clear();
  }

  fetchAll() {
    if (this.requests.get('all') === undefined) {
      this.requests.set('all', 'pending');
      return apiActions
        .v2RawGet('users/me/instructor_comments')
        .then(response => {
          this.addMany(response.data);
          this.requests.set('all', 'complete')
        })
    }
    return Promise.resolve();
  }

  refetch() {
    if (this.requests.get('all') !== undefined) {
      this.reset();
      return this.fetchAll();
    }
    this.reset();
    return Promise.resolve();
  }

  get fetchAllCompleted() {
    return this.requests.get('all') === 'complete';
  }

  create(params) {
    return apiActions.v2RawPost('users/me/instructor_comments', { instructor_internal_comment: params })
                     .then(response => this.add(response.data));
  }
}

export class InstructorInternalComment {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.admin = data.admin;
    this.comment = data.comment;
    this.potential_child_id = data.potential_child_id;
    this.student_id = data.student_id;
    this.instructor_id = data.instructor_id;
    this.event_id = data.event_id;
    this.lesson_id = data.lesson_id;
    this.inserted_at = data.inserted_at;
    this.updated_at = data.updated_at;
  }

  get studentUser() {
    return this._root.UserStore.find(this.student_id);
  }

  get studentChild() {
    return this.studentUser ? this._root.PotentialChildStore.forUser(this.student_id) : this._root.PotentialChildStore.find(this.potential_child_id);
  }

  get event() {
    return this._root.EventStore.find(this.event_id);
  }

  get sinceInserted() {
    return moment(this.inserted_at).fromNow();
  }

  get sinceUpdated() {
    return moment(this.updated_at).fromNow();
  }

  get wasEdited() {
    return this.inserted_at !== this.updated_at;
  }
}
